import Swiper from 'swiper';
import { Navigation, EffectFade, Pagination, Autoplay, A11y } from 'swiper/modules';
import { slidesPerViewValue, updateSlideAccessibility } from './slider-a11y.js';

const sliderModules = document.querySelectorAll('.module-slider:not([data-slider-init="manual"])');
const viewedSlides = new Set();
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const pushDataLayerEvent = (eventName, payload = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: eventName,
        ...payload,
    });
};

const getModuleName = (module) => {
    if (module.dataset.moduleName) {
        return module.dataset.moduleName;
    }

    const ignoreClassNames = new Set(['module-slider', 'module-hero']);
    const moduleClass = [...module.classList].find((className) => className.startsWith('module-') && !ignoreClassNames.has(className));
    return moduleClass ? moduleClass.replace('module-', '') : 'slider-module';
};

const getSlideTitle = (slideEl) => {
    if (!slideEl) {
        return '';
    }

    if (slideEl.dataset.slideTitle) {
        return slideEl.dataset.slideTitle;
    }

    const titleEl = slideEl.querySelector('.hero-title, h1, h2, h3, h4, h5, h6');
    return titleEl ? titleEl.textContent.trim() : '';
};

const getSlideMeta = (swiper, module) => {
    if (!swiper || !swiper.slides || typeof swiper.activeIndex === 'undefined') {
        return null;
    }

    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) {
        return null;
    }

    const normalizedIndex = typeof swiper.realIndex === 'number' ? swiper.realIndex + 1 : swiper.activeIndex + 1;
    const slideIndex = activeSlide.dataset.slideIndex ? Number(activeSlide.dataset.slideIndex) : normalizedIndex;
    const slideId = activeSlide.dataset.slideId || `${module.dataset.moduleName}-slide-${slideIndex}`;
    const slideTitle = getSlideTitle(activeSlide);

    return {
        slideEl: activeSlide,
        slideId,
        slideIndex,
        slideTitle,
    };
};

const getBasePayload = (module) => ({
    lifted_component: module.dataset.moduleName,
    lifted_module_position: Number(module.dataset.modulePosition),
    lifted_page_path: `${window.location.pathname}${window.location.search}`,
});

const assignSlideMetadata = (module, swiperEl) => {
    const slides = swiperEl.querySelectorAll('.swiper-slide');
    slides.forEach((slideEl, index) => {
        const slideIndex = index + 1;
        if (!slideEl.dataset.slideIndex) {
            slideEl.dataset.slideIndex = String(slideIndex);
        }
        if (!slideEl.dataset.slideId) {
            slideEl.dataset.slideId = `${module.dataset.moduleName}-${module.dataset.modulePosition}-slide-${slideIndex}`;
        }
        if (!slideEl.dataset.slideTitle) {
            slideEl.dataset.slideTitle = getSlideTitle(slideEl);
        }
    });
};


sliderModules.forEach(function (module, moduleIndex) {
    module.dataset.modulePosition = String(moduleIndex + 1);
    module.dataset.moduleName = getModuleName(module);

    let swiperEl = module.querySelector('.swiper');
    let next = module.querySelector('.slider-nav-next');
    let prev = module.querySelector('.slider-nav-prev');
    let pagination = module.querySelector('.slider-pagination');
    let numbers = module.querySelector('.slider-nav-numbers');

    if (swiperEl) {
        assignSlideMetadata(module, swiperEl);

        let columns = swiperEl.dataset.sliderColumns;
        let paginationEnabled = swiperEl.dataset.sliderPagination;
        let animation = swiperEl.dataset.sliderAnimation;
        let autoplayEnabled = swiperEl.dataset.sliderAutoplay;
        let autoplaySpeed = swiperEl.dataset.sliderAutoplaySpeed;
        let loop = swiperEl.dataset.sliderLoop;
        let centeredSlides = swiperEl.dataset.sliderCenteredSlides;
        let desktopSlidesPerView = swiperEl.dataset.sliderColumnsDesktop;
        let tabletSlidesPerView = swiperEl.dataset.sliderColumnsTablet;
        let mobileSlidesPerView = swiperEl.dataset.sliderColumnsMobile;

        // Swiper can behave oddly when both `loop` and `centeredSlides` are enabled
        // with very few slides (e.g. 3). If that happens, disable both for stability.
        const totalSlides = swiperEl.querySelectorAll('.swiper-slide').length;
        const moduleObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting || !entry.target.classList.contains('swiper-slide-active')) {
                    return;
                }

                const slideId = entry.target.dataset.slideId;
                const slideIndex = Number(entry.target.dataset.slideIndex) || 0;
                const viewKey = `${module.dataset.modulePosition}:${slideId || slideIndex}`;

                if (viewedSlides.has(viewKey)) {
                    return;
                }

                viewedSlides.add(viewKey);

                pushDataLayerEvent('lifted_slider_view_slide', {
                    ...getBasePayload(module),
                    lifted_action: 'view_slide',
                    lifted_slide_id: slideId,
                    lifted_slide_index: slideIndex,
                    lifted_slide_name: getSlideTitle(entry.target),
                });
            });
        }, {
            threshold: 0.5,
        });

        const observeSlideView = (swiper) => {
            const slideMeta = getSlideMeta(swiper, module);
            if (!slideMeta || !slideMeta.slideEl) {
                return;
            }

            moduleObserver.disconnect();
            moduleObserver.observe(slideMeta.slideEl);
        };

            if (totalSlides < 4 && loop && centeredSlides) {
                loop = false;
                centeredSlides = false;
            }

        let breakpoints = {
            320: {
                slidesPerView: slidesPerViewValue(mobileSlidesPerView, 1),
                spaceBetween: 8,
            },
            640: {
                slidesPerView: slidesPerViewValue(tabletSlidesPerView, 2),
                spaceBetween: 8,
            },
            1024: {
                slidesPerView: slidesPerViewValue(desktopSlidesPerView, 3),
                spaceBetween: 16,
            }
        }

        if (columns && columns == '4' || desktopSlidesPerView == '4') {
            breakpoints = {
                320: {
                    slidesPerView: slidesPerViewValue(mobileSlidesPerView, 1.1),
                    spaceBetween: 8,
                },
                500: {
                    slidesPerView: slidesPerViewValue(tabletSlidesPerView, 2),
                },
                640: {
                    slidesPerView: slidesPerViewValue(tabletSlidesPerView, 3),
                },
                1024: {
                    slidesPerView: slidesPerViewValue(desktopSlidesPerView, 4),
                    spaceBetween: 16,
                }
            }
        }

        if (columns && columns == '2' || desktopSlidesPerView == '2') {
            breakpoints = {
                640: {
                    slidesPerView: slidesPerViewValue(desktopSlidesPerView, 2),
                    spaceBetween: 16,
                }
            }
        }

        if (columns && columns == '1' || desktopSlidesPerView == '1') {
            breakpoints = {
                640: {
                    slidesPerView: slidesPerViewValue(desktopSlidesPerView, 1),
                    spaceBetween: 16,
                }
            }
        }

        let updateSliderNumbers = function (index, length) {
            if (numbers) {
                numbers.innerHTML = index + 1 + '/' + length;
            }
        }

        let swiperParams = {
            modules: [Navigation, A11y],
            slidesPerView: 1,
            spaceBetween: 8,
            watchSlidesProgress: true,
            a11y: {
                enabled: true,
                // Don't override native semantics on slides that are links/buttons.
                // APG uses role="group" on container slides, not on the interactive control itself.
                slideRole: null,
                slideLabelMessage: null,
                prevSlideMessage: 'Previous slide',
                nextSlideMessage: 'Next slide',
                paginationBulletMessage: 'Go to slide {{index}}',
            },
            navigation: {
                nextEl: next,
                prevEl: prev,
            },
            breakpoints: breakpoints,
            on: {
                init: function (swiper) {
                    updateSliderNumbers(swiper.activeIndex, swiper.slides.length);
                    observeSlideView(swiper);
                    updateSlideAccessibility(swiper);
                },
                activeIndexChange: function (swiper) {
                    updateSliderNumbers(swiper.activeIndex, swiper.slides.length);
                    observeSlideView(swiper);
                    updateSlideAccessibility(swiper);
                },
                slideChangeTransitionEnd: function (swiper) {
                    updateSlideAccessibility(swiper);
                },
                resize: function (swiper) {
                    updateSlideAccessibility(swiper);
                },
            },
        };

        if (animation && animation == 'fade') {
            swiperParams.modules.push(EffectFade);
            swiperParams.effect = 'fade';
            swiperParams.fadeEffect = {
                crossFade: true
            };
        }

        if (paginationEnabled) {
            swiperParams.modules.push(Pagination);
            swiperParams.pagination = {
                el: pagination,
                type: 'bullets',
                clickable: true
            };
        }

        if (autoplayEnabled && autoplaySpeed && !prefersReducedMotion) {
            swiperParams.autoplay = {
                delay: autoplaySpeed * 1000,
                disableOnInteraction: true
            };
            swiperParams.loop = true;
            swiperParams.modules.push(Autoplay);
        }

        if (loop) {
            swiperParams.loop = true;
        }

        if (centeredSlides) {
            swiperParams.centeredSlides = true;
        }

        // remove navigation if there is only 1 slide
        if (totalSlides <= 1 && next && prev) {
            next.style.display = 'none';
            prev.style.display = 'none';
        }
        
        const swiper = new Swiper(swiperEl, swiperParams);

        // Pause autoplay while keyboard focus is inside the slider (WCAG 2.2.2).
        if (swiper.autoplay) {
            swiperEl.addEventListener('focusin', () => {
                if (swiper.autoplay.running) {
                    swiper.autoplay.pause();
                }
            });

            swiperEl.addEventListener('focusout', (event) => {
                if (swiperEl.contains(event.relatedTarget)) {
                    return;
                }
                if (swiper.autoplay && !swiper.autoplay.running) {
                    swiper.autoplay.resume();
                }
            });
        }

        module.addEventListener('click', function (event) {
            const ctaEl = event.target.closest('.module-buttons a');
            if (!ctaEl || !module.contains(ctaEl)) {
                return;
            }

            const slideMeta = getSlideMeta(swiper, module);
            if (!slideMeta) {
                return;
            }

            pushDataLayerEvent('lifted_slider_click_cta', {
                ...getBasePayload(module),
                lifted_action: 'click_cta',
                lifted_slide_id: slideMeta.slideId,
                lifted_slide_index: slideMeta.slideIndex,
                lifted_slide_name: slideMeta.slideTitle,
                lifted_label: ctaEl.textContent.trim(),
                lifted_target_url: ctaEl.href || '',
                lifted_target: ctaEl.target || '_self',
            });
        });

        if (next) {
            next.addEventListener('click', function () {
                const slideMeta = getSlideMeta(swiper, module);
                if (!slideMeta) {
                    return;
                }

                pushDataLayerEvent('lifted_slider_click_arrow', {
                    ...getBasePayload(module),
                    lifted_action: 'click_arrow',
                    lifted_slide_id: slideMeta.slideId,
                    lifted_slide_index: slideMeta.slideIndex,
                    lifted_slide_name: slideMeta.slideTitle,
                    lifted_direction: 'next',
                });
            });
        }

        if (prev) {
            prev.addEventListener('click', function () {
                const slideMeta = getSlideMeta(swiper, module);
                if (!slideMeta) {
                    return;
                }

                pushDataLayerEvent('lifted_slider_click_arrow', {
                    ...getBasePayload(module),
                    lifted_action: 'click_arrow',
                    lifted_slide_id: slideMeta.slideId,
                    lifted_slide_index: slideMeta.slideIndex,
                    lifted_slide_name: slideMeta.slideTitle,
                    lifted_direction: 'prev',
                });
            });
        }

        if (pagination) {
            pagination.addEventListener('click', function (event) {
                const bullet = event.target.closest('.swiper-pagination-bullet');
                if (!bullet) {
                    return;
                }

                const bullets = [...pagination.querySelectorAll('.swiper-pagination-bullet')];
                const bulletIndex = bullets.indexOf(bullet);
                const slideMeta = getSlideMeta(swiper, module);

                pushDataLayerEvent('lifted_slider_click_pagination', {
                    ...getBasePayload(module),
                    lifted_action: 'click_pagination',
                    lifted_slide_id: slideMeta ? slideMeta.slideId : '',
                    lifted_slide_index: slideMeta ? slideMeta.slideIndex : 0,
                    lifted_slide_name: slideMeta ? slideMeta.slideTitle : '',
                    lifted_pagination_index: bulletIndex >= 0 ? bulletIndex + 1 : 0,
                });
            });
        }
    }
});